import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert } from 'react-bootstrap';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { authHeaders, getAuthToken, getAutomateApiUrl } from '../automate/apiBase';
import './Profile.css';

const TABS = [
  { id: 'account', label: 'Account' },
  { id: 'usage', label: 'Plan & usage' },
  { id: 'integrations', label: 'Integrations' },
];

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRole(role) {
  if (!role) return '—';
  const labels = {
    admin: 'Admin',
    super_admin: 'Super admin',
    SYSTEM_ADMIN: 'Platform admin',
    USER: 'Standard user',
    user: 'User',
    ORG_ADMIN: 'Org admin',
    PROJECT_MANAGER: 'Project manager',
    LEAD_QA: 'Lead QA',
    TESTER: 'Tester',
    AUDITOR: 'Auditor',
  };
  return labels[role] || String(role).replace(/_/g, ' ');
}

function formatPlan(plan) {
  if (!plan) return '—';
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

function getInitials(me) {
  if (me?.firstName && me?.lastName) {
    return (me.firstName.charAt(0) + me.lastName.charAt(0)).toUpperCase();
  }
  if (me?.username) return me.username.charAt(0).toUpperCase();
  return '?';
}

function displayName(me) {
  if (me?.firstName && me?.lastName) return `${me.firstName} ${me.lastName}`;
  return me?.firstName || me?.username || 'User';
}

function syncUserProfile(setUserProfile, me) {
  if (typeof setUserProfile !== 'function' || !me) return;
  setUserProfile({
    firstName: me.firstName,
    lastName: me.lastName,
    email: me.email,
    phoneNumber: me.phoneNumber,
    username: me.username,
    role: me.role,
    systemRole: me.systemRole,
    tenantRole: me.tenantRole,
    department: me.department,
    profilePhoto: me.profilePhoto,
  });
}

const ProfileRow = ({ label, value, children }) => (
  <div className="profile-row">
    <div className="profile-label">{label}</div>
    <div className="profile-value">{children ?? value ?? '—'}</div>
  </div>
);

const Profile = ({ setUserProfile }) => {
  const [me, setMe] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [activeTab, setActiveTab] = useState('account');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertVariant, setAlertVariant] = useState('danger');
  const [openAiStatus, setOpenAiStatus] = useState({ hasApiKey: false, loading: true });
  const [openAiKeyInput, setOpenAiKeyInput] = useState('');
  const [savingKey, setSavingKey] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const loadData = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }
    try {
      const decoded = jwtDecode(token);
      setTenantId(decoded.tenant_id ?? null);
    } catch {
      setTenantId(null);
    }

    try {
      const [meRes, keyRes] = await Promise.all([
        axios.get(`${getAutomateApiUrl()}/api/auth/me`, { headers: authHeaders() }),
        axios.get(`${getAutomateApiUrl()}/api/user/openai-key`, { headers: authHeaders() }),
      ]);
      const meData = meRes.data || {};
      setMe(meData);
      syncUserProfile(setUserProfile, meData);
      setOpenAiStatus({
        hasApiKey: Boolean(keyRes.data?.hasApiKey),
        loading: false,
      });
      setError('');
    } catch (err) {
      console.error('Failed to load profile:', err);
      const msg = err.response?.data?.error
        || err.response?.data?.message
        || err.response?.data?.detail
        || 'Failed to load profile';
      setError(typeof msg === 'string' ? msg : 'Failed to load profile');
      setAlertMessage('Failed to load profile');
      setAlertVariant('danger');
    } finally {
      setLoading(false);
    }
  }, [setUserProfile]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showAlert = (message, variant = 'success') => {
    setAlertMessage(message);
    setAlertVariant(variant);
  };

  const handlePhotoSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showAlert('Please choose an image file.', 'danger');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showAlert('Image must be 2 MB or smaller.', 'danger');
      return;
    }
    setUploadingPhoto(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await axios.post(
        `${getAutomateApiUrl()}/api/auth/profile/photo`,
        { profilePhoto: dataUrl },
        { headers: authHeaders() },
      );
      setMe((prev) => {
        const next = { ...prev, profilePhoto: dataUrl };
        syncUserProfile(setUserProfile, next);
        return next;
      });
      showAlert('Profile photo updated.', 'success');
    } catch (err) {
      console.error('Photo upload failed:', err);
      showAlert('Failed to update profile photo.', 'danger');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    setUploadingPhoto(true);
    try {
      await axios.post(
        `${getAutomateApiUrl()}/api/auth/profile/photo`,
        { profilePhoto: null },
        { headers: authHeaders() },
      );
      setMe((prev) => {
        const next = { ...prev, profilePhoto: null };
        syncUserProfile(setUserProfile, next);
        return next;
      });
      showAlert('Profile photo removed.', 'success');
    } catch (err) {
      console.error('Remove photo failed:', err);
      showAlert('Failed to remove profile photo.', 'danger');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaveOpenAiKey = async (e) => {
    e.preventDefault();
    setSavingKey(true);
    try {
      await axios.post(
        `${getAutomateApiUrl()}/api/user/openai-key`,
        { openaiApiKey: openAiKeyInput.trim() },
        { headers: authHeaders() },
      );
      setOpenAiKeyInput('');
      setOpenAiStatus({ hasApiKey: true, loading: false });
      showAlert('OpenAI API key saved.', 'success');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'object' ? detail.message : detail;
      showAlert(message || 'Failed to save API key.', 'danger');
    } finally {
      setSavingKey(false);
    }
  };

  const handleRemoveOpenAiKey = async () => {
    setSavingKey(true);
    try {
      await axios.delete(`${getAutomateApiUrl()}/api/user/openai-key`, { headers: authHeaders() });
      setOpenAiStatus({ hasApiKey: false, loading: false });
      showAlert('OpenAI API key removed.', 'success');
    } catch (err) {
      showAlert('Failed to remove API key.', 'danger');
    } finally {
      setSavingKey(false);
    }
  };

  const tokenLimit = me?.tokenLimit;
  const tokensUsed = Number(me?.totalTokensUsed || 0);
  const tokenLimitLabel =
    tokenLimit === null ? 'Unlimited' : tokenLimit === undefined ? '—' : Number(tokenLimit).toLocaleString();
  const usagePercent =
    tokenLimit != null && tokenLimit > 0
      ? Math.min(100, Math.round((tokensUsed / tokenLimit) * 100))
      : null;

  return (
    <div className="profile-page-bg">
      <div className="profile-container">
        {loading && (
          <div className="loading">
            <p>Loading profile data...</p>
          </div>
        )}
        {error && !loading && (
          <div className="error">
            <p>{error}</p>
            <button type="button" onClick={() => { window.location.href = '/login'; }}>
              Go to Login
            </button>
          </div>
        )}
        {!loading && !error && me && (
          <>
            <div className="profile-header">
              {me.profilePhoto ? (
                <img src={me.profilePhoto} alt="" className="profile-photo" />
              ) : (
                <div className="profile-photo-placeholder" aria-hidden="true">
                  {getInitials(me)}
                </div>
              )}
              <h2>{displayName(me)}</h2>
              <p className="profile-header-subtitle">
                @{me.username}
                {me.systemRole ? (
                  <span className="profile-role-badge">{formatRole(me.systemRole)}</span>
                ) : null}
                {me.tenantRole ? (
                  <span className="profile-role-badge profile-role-badge-tenant">
                    {formatRole(me.tenantRole)}
                  </span>
                ) : null}
              </p>
              <div className="profile-header-actions">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoSelect}
                />
                <button
                  type="button"
                  className="upload-photo-btn"
                  disabled={uploadingPhoto}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadingPhoto ? 'Uploading…' : 'Upload photo'}
                </button>
                {me.profilePhoto && (
                  <button
                    type="button"
                    className="upload-photo-btn remove-photo-btn"
                    disabled={uploadingPhoto}
                    onClick={handleRemovePhoto}
                  >
                    Remove photo
                  </button>
                )}
              </div>
            </div>

            {alertMessage && (
              <Alert
                variant={alertVariant}
                dismissible
                onClose={() => setAlertMessage(null)}
                className="profile-alert"
              >
                {alertMessage}
              </Alert>
            )}

            <div className="profile-tabs" role="tablist">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`profile-tab${activeTab === tab.id ? ' active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="profile-data">
              {activeTab === 'account' && (
                <>
                  <h3>Account details</h3>
                  <ProfileRow label="Email" value={me.email} />
                  <ProfileRow label="Email verified">
                    {me.emailVerified ? (
                      <span className="profile-badge profile-badge-success">Verified</span>
                    ) : (
                      <span className="profile-badge profile-badge-muted">Not verified</span>
                    )}
                  </ProfileRow>
                  <ProfileRow label="Platform role">
                    {me.systemRole ? (
                      <span className="profile-badge">{formatRole(me.systemRole)}</span>
                    ) : (
                      '—'
                    )}
                  </ProfileRow>
                  <ProfileRow label="Organization role">
                    {me.tenantRole ? (
                      <span className="profile-badge">{formatRole(me.tenantRole)}</span>
                    ) : (
                      '—'
                    )}
                  </ProfileRow>
                  <ProfileRow label="Phone" value={me.phoneNumber || 'Not set'} />
                  <ProfileRow label="Department" value={me.department || 'Not set'} />
                  <ProfileRow label="User ID" value={me.id != null ? String(me.id) : '—'} />
                  {tenantId != null && (
                    <ProfileRow label="Current tenant ID" value={String(tenantId)} />
                  )}
                  <ProfileRow label="Sign-in method">
                    {me.microsoftId ? 'Microsoft SSO' : me.password ? 'Email & password' : 'Single sign-on'}
                  </ProfileRow>
                  <ProfileRow label="Member since" value={formatDate(me.createdAt)} />
                  <ProfileRow label="Profile updated" value={formatDateTime(me.updatedAt)} />
                </>
              )}

              {activeTab === 'usage' && (
                <>
                  <h3>Plan & token usage</h3>
                  <ProfileRow label="Plan" value={formatPlan(me.plan)} />
                  {me.trialPeriodEndDate && (
                    <ProfileRow label="Trial ends" value={formatDate(me.trialPeriodEndDate)} />
                  )}
                  {me.subscriptionEndDate && me.plan === 'paid' && (
                    <ProfileRow label="Subscription ends" value={formatDate(me.subscriptionEndDate)} />
                  )}
                  {me.lastTokenResetDate && me.plan === 'free' && (
                    <ProfileRow label="Token period started" value={formatDate(me.lastTokenResetDate)} />
                  )}
                  <ProfileRow label="Tokens used" value={tokensUsed.toLocaleString()} />
                  <ProfileRow label="Token limit" value={tokenLimitLabel} />
                  {usagePercent != null && (
                    <div className="profile-token-usage">
                      <div className="profile-token-usage-label">
                        <span>Usage</span>
                        <span>
                          {tokensUsed.toLocaleString()}
                          {' / '}
                          {Number(tokenLimit).toLocaleString()}
                          {' '}
                          (
                          {usagePercent}
                          %)
                        </span>
                      </div>
                      <div className="profile-token-usage-track">
                        <div
                          className={`profile-token-usage-fill${me.tokenLimitExceeded ? ' exceeded' : ''}`}
                          style={{ width: `${usagePercent}%` }}
                        />
                      </div>
                      {me.tokenLimitExceeded && (
                        <p className="profile-token-warning">Token limit reached for your current plan.</p>
                      )}
                    </div>
                  )}
                  {tokenLimit === null && me.plan === 'paid' && (
                    <p className="profile-token-note">Paid plan: no token cap while subscription is active.</p>
                  )}
                </>
              )}

              {activeTab === 'integrations' && (
                <>
                  <h3>Chat Assist (OpenAI)</h3>
                  <div className="profile-section api-key-section">
                    {openAiStatus.loading ? (
                      <p className="api-key-info">Checking API key status…</p>
                    ) : openAiStatus.hasApiKey ? (
                      <div className="api-key-status">
                        <p>
                          <strong>Your OpenAI key is saved.</strong>
                          {' '}
                          Chat Assist can use your personal key. The key is stored securely and not shown here.
                        </p>
                        <button
                          type="button"
                          className="upload-photo-btn remove-photo-btn"
                          disabled={savingKey}
                          onClick={handleRemoveOpenAiKey}
                        >
                          Remove key
                        </button>
                      </div>
                    ) : (
                      <div className="api-key-input">
                        <p className="api-key-info">
                          Add your OpenAI API key to enable Chat Assist features that require your own key.
                        </p>
                        <form className="api-key-form" onSubmit={handleSaveOpenAiKey}>
                          <input
                            type="password"
                            className="api-key-input-field"
                            placeholder="sk-..."
                            value={openAiKeyInput}
                            onChange={(e) => setOpenAiKeyInput(e.target.value)}
                            autoComplete="off"
                          />
                          <button type="submit" className="upload-photo-btn" disabled={savingKey || !openAiKeyInput.trim()}>
                            {savingKey ? 'Saving…' : 'Save API key'}
                          </button>
                        </form>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;
